<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_Name')"
        :hint="$t('Integrations_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_ConnectionSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_SftpUrl')"
        prop="params.url"
      >
        <el-input
          v-model="model.params.url"
          :placeholder="$t('Integrations_Field_SftpUrl')"
        />
      </rest-form-item>

      <rest-form-item
        prop="params.connection_type"
        :label="$t('Integrations_Settings_ConnectionType')"
      >
        <el-row>
          <el-col :span="12">
            <radio-group v-model="model.params.connection_type" inline>
              <radio
                label="0"
                :title="
                  $t('Integrations_Settings_ConnectionType_UsernamePassword')
                "
                :description="
                  $t(
                    'Integrations_Settings_ConnectionType_UsernamePassword_Description'
                  )
                "
              />
              <radio
                label="1"
                :title="$t('Integrations_Settings_ConnectionType_SshKey')"
                :description="
                  $t('Integrations_Settings_ConnectionType_SshKey_Description')
                "
              />
            </radio-group>
          </el-col>

          <el-col v-if="model.params.ssh_key_pair" :span="12">
            <rest-form-item
              :label="$t('Integrations_Field_SftpKey')"
              prop="params.ssh_key_pair"
            >
              <integration-key-box :ssh-key="selectedKeySSH" />
            </rest-form-item>
          </el-col>
        </el-row>
      </rest-form-item>

      <el-row v-if="model.params.connection_type !== undefined">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Field_SftpUsername')"
            prop="params.username"
          >
            <el-input
              v-model="model.params.username"
              :placeholder="$t('Integrations_Field_SftpUsername')"
            />
          </rest-form-item>
        </el-col>
        <el-col :span="12">
          <rest-form-item
            v-if="model.params.connection_type === '0'"
            :label="$t('Integrations_Field_SftpPassword')"
            prop="params.password"
          >
            <el-input
              v-model="model.params.password"
              :placeholder="$t('Integrations_Field_SftpPassword')"
            />
          </rest-form-item>
          <rest-form-item
            v-else-if="model.params.connection_type === '1'"
            :label="$t('Integrations_Sftp_Field_SshKey')"
            prop="params.ssh_key_pair"
          >
            <el-autocomplete
              class="inline-input"
              v-model="selectedKey"
              :fetch-suggestions="fetchKeys"
              :placeholder="$t('Integrations_Sftp_Text_SelectSshKey')"
              @select="handleSelect"
            >
              <el-button
                slot="append"
                icon="el-icon-key"
                @click="showKeyDialog = true"
                >{{ $t('Integrations_Sftp_Text_GenerateNewSshKey') }}</el-button
              >
            </el-autocomplete>
          </rest-form-item>
        </el-col>
      </el-row>

      <rest-form-item
        :label="$t('Integrations_Field_SftpPort')"
        prop="params.port"
      >
        <el-input
          v-model="model.params.port"
          :placeholder="$t('Integrations_Field_SftpPort')"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Field_SftpFolderName')"
        prop="params.folder"
      >
        <el-input
          v-model="model.params.folder"
          :placeholder="$t('Integrations_Field_SftpFolderName_Placeholder')"
        />
      </rest-form-item>
    </form-section>

    <form-section
      v-if="isProxyEnabled"
      :title="$t('Integrations_Sftp_Title_OtherSettings')"
    >
      <integration-proxy-settings
        v-model="model.params.other_settings.use_static_ip"
      />
    </form-section>

    <ssh-key-generate-dialog
      :visible.sync="showKeyDialog"
      :show-hint="true"
      @created="onKeyCreated"
    />
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import Radio from '@/common/components/form/Radio'
import RadioGroup from '@/common/components/form/RadioGroup'
import IntegrationKeyBox from '@/modules/integrations/components/IntegrationKeyBox'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import store from '@/core/store'
import IntegrationProxySettings from '@/modules/integrations/components/IntegrationProxySettings.vue'
import SshKeyGenerateDialog from '@/common/components/ssh/SshKeyGenerateDialog.vue'

export default {
  name: 'SftpSettings',

  components: {
    IntegrationProxySettings,
    FormSection,
    RestFormItem,
    Radio,
    RadioGroup,
    IntegrationKeyBox,
    SshKeyGenerateDialog
  },

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  created() {
    if (this.isCreatePage) {
      this.setDefaultOtherSettings()
    } else if (this.model.params.other_settings === undefined) {
      this.setDefaultOtherSettings()
    }
  },

  data() {
    return {
      showKeyDialog: false,
      selectedKey: null,
      selectedKeySSH: '',
      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          connection_type: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          url: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          username: {
            required:
              this.model.params.connection_type === undefined ||
              this.model.params.connection_type === '0',
            message: this.$t('RestForm_FieldIsRequired')
          },
          password: {
            required:
              this.model.params.connection_type === undefined ||
              this.model.params.connection_type === '0',
            message: this.$t('RestForm_FieldIsRequired')
          },
          public_key: {
            required:
              this.model.params.connection_type === undefined ||
              this.model.params.connection_type === '1',
            message: this.$t('RestForm_FieldIsRequired')
          },
          port: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          }
        }
      }
    }
  },

  mounted() {
    if (this.$route.query.id && this.model.params.ssh_key_pair) {
      RestApi.get(
        `projects/${this.$store.getters.projectId}/ssh-keys/${this.model.params.ssh_key_pair}`
      )
        .then(({ data }) => {
          this.selectedKey = data.name
          this.selectedKeySSH = data.public_key
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  },

  computed: {
    fetchKeysEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `ssh-keys`
      })
    },
    isProxyEnabled() {
      return store.state.app.proxy ? store.state.app.proxy['enabled'] : null
    },
    isCreatePage() {
      return !this.$route.query.id
    }
  },

  methods: {
    fetchKeys(queryString, cb) {
      RestApi({
        ...this.fetchKeysEndpoint,
        params: {
          name: queryString
        }
      })
        .then(({ data }) => {
          let list = data.reduce((arr, key) => {
            arr.push({
              value: key.name,
              id: key.id,
              public_key: key.public_key
            })
            return arr
          }, [])

          cb(list)
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    onKeyCreated(data) {
      this.model.params.ssh_key_pair = data.id
      this.selectedKey = data.name
      this.selectedKeySSH = data.public_key
    },

    handleSelect(item) {
      if (item.id) {
        this.model.params.ssh_key_pair = item.id
        this.selectedKeySSH = item.public_key
      }
    },
    setDefaultOtherSettings() {
      this.$set(this.model.params, 'other_settings', {
        use_static_ip: 0
      })
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/integrations/types/sftp/SftpSettings.vue